import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { getPriceHistory } from "../../../services";
import AppLoader from "../AppLoader";

interface VariantInfo {
  id: number;
  name: string;
}

interface PriceHistoryChartProps {
  productId: number;
  variants: VariantInfo[];
}

interface ChartData {
  snapshotDate: string;
  [key: string]: number | string;
}

const PriceHistoryChart: React.FC<PriceHistoryChartProps> = ({
  productId,
  variants,
}) => {
  const [data, setData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Generate distinct colors dynamically (handles unlimited variants)
  const generateColor = (index: number) => {
    const hue = (index * 137.508) % 360; // Golden angle for distinct colors
    return `hsl(${hue}, 70%, 50%)`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const results = await Promise.all(
          variants.map((v) => getPriceHistory(productId, v.id))
        );

        console.log("Raw results:", results);

        const dataMap: Record<string, ChartData> = {};

        results.forEach((variantData, i) => {
          const variantKey = `Variant-${variants[i].id}`;

          variantData
            .filter((item) => item.currentPrice !== null)
            .forEach((item) => {
              const dateKey = new Date(item.snapshotDate).toISOString();

              if (!dataMap[dateKey]) {
                dataMap[dateKey] = { snapshotDate: dateKey };
              }

              dataMap[dateKey][variantKey] = parseFloat(item.currentPrice);
            });
        });

        console.log("Built dataMap:", dataMap);

        const combined = Object.values(dataMap).sort(
          (a, b) =>
            new Date(a.snapshotDate).getTime() -
            new Date(b.snapshotDate).getTime()
        );

        console.log("Combined data:", combined);

        setData(combined);
      } catch (error) {
        console.error("Error fetching price history", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId, variants]);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center my-3">
        <AppLoader size={32} thickness={4} color="#38B2AC" />
      </div>
    );

  if (data.length === 0)
    return (
      <p className="text-muted small text-center mb-0">No price history</p>
    );

  return (
    <div style={{ width: "100%", height: 250 }}>
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="snapshotDate"
            tickFormatter={(value) =>
              new Date(value).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })
            }
            tick={{ fontSize: 12 }}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            labelFormatter={(value) =>
              new Date(value).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            }
          />
          <Legend />
          {variants.map((v, i) => (
            <Line
              key={`Variant-${v.id}`}
              type="monotone"
              dataKey={`Variant-${v.id}`}
              stroke={generateColor(i)}
              strokeWidth={2}
              dot={{ r: 3 }}
              name={v.name || `Variant ${i + 1}`}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PriceHistoryChart;
